import { FormEvent, useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../../components/Skeleton.js";
import { useTableState } from "../../lib/useTableState.js";
import TableToolbar from "../../components/TableToolbar.js";
import { Link, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../../lib/api.js";
import EmptyState from "../../components/EmptyState.js";
import Drawer from "../../components/Drawer.js";

type Entry = Awaited<ReturnType<typeof api.auditFiltered>>["entries"][number];

export default function AdminAudit() {
  const [params] = useSearchParams();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [actor, setActor] = useState(params.get("actor") ?? "");
  const [action, setAction] = useState(params.get("action") ?? "");
  const [workspaceId, setWorkspaceId] = useState(params.get("workspaceId") ?? "");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [detail, setDetail] = useState<Entry | null>(null);

  const reversed = useMemo(() => entries.slice().reverse(), [entries]);
  const t = useTableState(reversed as unknown as Array<Record<string, unknown>>, {
    searchFields: ["action", "actorUserId", "actorRole", "workspaceId"],
    initialPageSize: 25
  });

  const load = (overrides?: Partial<{ actor: string; action: string; workspaceId: string; from: string; to: string }>) => {
    setLoading(true);
    setErr(null);
    const params = {
      actor: overrides?.actor ?? actor,
      action: overrides?.action ?? action,
      workspaceId: overrides?.workspaceId ?? workspaceId,
      from: overrides?.from ?? from,
      to: overrides?.to ?? to,
      limit: 500
    };
    api.auditFiltered(params)
      .then((d) => setEntries(d.entries ?? []))
      .catch((e) => setErr(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load({}); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const onExportCsv = async () => {
    try {
      await api.downloadAuditCsv({
        actor: actor || undefined,
        action: action || undefined,
        workspaceId: workspaceId || undefined,
        from: from || undefined,
        to: to || undefined,
        limit: 50000
      });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    load();
  };
  const onReset = () => {
    setActor(""); setAction(""); setWorkspaceId(""); setFrom(""); setTo("");
    load({ actor: "", action: "", workspaceId: "", from: "", to: "" });
  };

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Audit log</h1>
          <p>Platform-wide audit trail. Filter by actor, action type, workspace, or date range.</p>
        </div>
      </header>

      <form className="card" onSubmit={onSubmit} style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12 }}>
          <div className="field">
            <label>Actor (user id or role)</label>
            <input type="text" value={actor} onChange={(e) => setActor(e.target.value)} placeholder="mem-..." />
          </div>
          <div className="field">
            <label>Action contains</label>
            <input type="text" value={action} onChange={(e) => setAction(e.target.value)} placeholder="secret, subscription.*" />
          </div>
          <div className="field">
            <label>Workspace id</label>
            <input type="text" value={workspaceId} onChange={(e) => setWorkspaceId(e.target.value)} placeholder="ws-..." />
          </div>
          <div className="field">
            <label>From</label>
            <input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="field">
            <label>To</label>
            <input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        </div>
        <div className="card-foot" style={{ display: "flex", gap: 8, padding: 12 }}>
          <button type="submit" className="btn btn-primary">Apply filters</button>
          <button type="button" className="btn" onClick={onReset}>Reset</button>
          <button type="button" className="btn" onClick={() => void onExportCsv()} disabled={entries.length === 0}>
            Export CSV ({entries.length})
          </button>
        </div>
      </form>

      {err && <div className="alert alert-error">{err}</div>}

      {loading ? <SkeletonTable rows={5} columns={4} /> :
       entries.length === 0 ? (
         <EmptyState
           icon="📋"
           title="No audit entries match"
           description="Audit entries appear after authenticated actions: signup, plan changes, secret rotations, member invites, and platform-admin overrides. Try widening the date range or clearing the actor filter."
         />
       ) : (
        <div>
          <TableToolbar
            hideExport
            search={t.search}
            setSearch={t.setSearch}
            page={t.page}
            setPage={t.setPage}
            pageSize={t.pageSize}
            setPageSize={t.setPageSize}
            totalPages={t.totalPages}
            totalRows={t.totalRows}
            searchPlaceholder="Search action / actor / workspace…"
          />
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr><th>When</th><th>Action</th><th>Actor</th><th>Workspace</th><th>Details</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {(t.paged as unknown as Entry[]).map((e) => (
                <tr key={e.id}>
                  <td className="soft">{new Date(e.occurredAt).toLocaleString()}</td>
                  <td><code>{e.action}</code></td>
                  <td className="soft">
                    <span className="pill pill-muted">{e.actorRole}</span>{" "}
                    {e.actorUserId ? <code style={{ fontSize: 12 }}>{e.actorUserId}</code> : null}
                  </td>
                  <td className="soft">{e.workspaceId ? <code>{e.workspaceId}</code> : "—"}</td>
                  <td className="soft" style={{ maxWidth: 440, whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12 }}>
                    {Object.keys(e.details).length === 0 ? "—" : JSON.stringify(e.details)}
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button className="btn btn-sm" type="button" onClick={() => setDetail(e)}>View</button>
                      {e.workspaceId && (
                        <Link className="btn btn-sm" to={`/admin/workspaces/${encodeURIComponent(e.workspaceId)}`}>Workspace</Link>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>
        </div>
       )}

      <Drawer
        open={detail != null}
        title="Audit entry"
        subtitle={detail ? <code>{detail.id}</code> : undefined}
        onClose={() => setDetail(null)}
        width={620}
        footer={<button type="button" className="btn" onClick={() => setDetail(null)}>Close</button>}
      >
        {detail && (
          <div style={{ display: "grid", gap: 8 }}>
            <div><strong>When:</strong> <span className="soft">{new Date(detail.occurredAt).toLocaleString()}</span></div>
            <div><strong>Action:</strong> <code>{detail.action}</code></div>
            <div><strong>Actor:</strong> <span className="pill pill-muted">{detail.actorRole}</span> {detail.actorUserId ? <code>{detail.actorUserId}</code> : null}</div>
            <div><strong>Workspace:</strong> {detail.workspaceId ? <code>{detail.workspaceId}</code> : "—"}</div>
            <div>
              <strong>Details:</strong>
              <pre style={{ background: "var(--surface-alt)", padding: 12, borderRadius: 6, overflow: "auto", maxHeight: 360 }}>
                {JSON.stringify(detail.details, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
